import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import messages from "../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import { eventStatuses } from "../../../utils/Constants";
import {
  getCompanyDashboardClientSatisfactionRequest,
  getCompanyDashboardOngoingEventsRequest,
  getCompanyDashboardOverviewRequest,
  getCompanyDashboardRecentActivityRequest,
  getCompanyDashboardTopEventsRequest,
} from "../../../store/CompanyPortal/Dashboard/actions";
import DashboardHeader from "../../../components/Dashboard/DashboardHeader";
import DashboardRefresh from "../../../components/Dashboard/DashboardRefresh";
import OngoingEventsData from "../../../components/Dashboard/OngoingEventsData";
import RecentActivityLog from "../../../components/Dashboard/RecentActivityLog";
import {
  COMPANY_ACTIVITY_MODULE_BY_API_KEY,
  toActivityLogRow,
} from "../../../components/Dashboard/RecentActivityLog/constants";
import SatisfactionSection from "../../../components/Dashboard/SatisfactionSection";
import TopEventsTable from "../../../components/Dashboard/TopEventsTable";
import TotalEventsChart from "../../../components/Dashboard/TotalEventsChart";
import "./Dashboard.scss";

/**
 * Maps the donut / KPI's `id` (design labels) to the API's `events_by_status
 * .counts` key (PROLINE-3755). `past` → "Completed", `canceled` → "Cancelled".
 */
const EVENT_STATUS_API_KEY = {
  completed: eventStatuses.past,
  upcoming: eventStatuses.upcoming,
  ongoing: eventStatuses.ongoing,
  draft: eventStatuses.draft,
  pending: eventStatuses.pendingPayment,
  cancelled: eventStatuses.cancelled,
};

/** The API calls a completed event `past`; the table's status labels/colours call it `completed`. */
const TOP_EVENT_STATUS = { past: "completed" };

const formatDateRange = (start, end, lang) => {
  if (!start || !end) return "--";
  const fmt = new Intl.DateTimeFormat(
    lang === "ar" ? "ar-EG-u-nu-latn" : "en-US",
    { day: "2-digit", month: "short", year: "numeric" },
  );
  return `${fmt.format(new Date(start))} – ${fmt.format(new Date(end))}`;
};

const formatCount = (value) => Number(value).toLocaleString("en-US");

const CompanyDashboardPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const overview = useSelector((state) => state.companyDashboard.overview);
  const overviewLoading = useSelector(
    (state) => state.companyDashboard.overviewLoading,
  );
  const ongoingEventsData = useSelector(
    (state) => state.companyDashboard.ongoingEvents,
  );
  const ongoingEventsLoading = useSelector(
    (state) => state.companyDashboard.ongoingEventsLoading,
  );
  const clientSatisfaction = useSelector(
    (state) => state.companyDashboard.clientSatisfaction,
  );
  const clientSatisfactionLoading = useSelector(
    (state) => state.companyDashboard.clientSatisfactionLoading,
  );
  const topEvents = useSelector((state) => state.companyDashboard.topEvents);
  const topEventsLoading = useSelector(
    (state) => state.companyDashboard.topEventsLoading,
  );
  const recentActivity = useSelector(
    (state) => state.companyDashboard.recentActivity,
  );
  const recentActivityLoading = useSelector(
    (state) => state.companyDashboard.recentActivityLoading,
  );
  const { dashboard, shared, activityLog } = messages[lang];

  const [updatedAt, setUpdatedAt] = useState(() => Date.now());

  /** Re-fetches every section's data and re-stamps "updated". */
  const fetchDashboard = useCallback(() => {
    dispatch(getCompanyDashboardOverviewRequest());
    dispatch(getCompanyDashboardTopEventsRequest());
    dispatch(getCompanyDashboardOngoingEventsRequest());
    dispatch(getCompanyDashboardClientSatisfactionRequest());
    dispatch(getCompanyDashboardRecentActivityRequest());
    setUpdatedAt(Date.now());
  }, [dispatch]);

  // `lang` re-fetches on a language switch: the API answers per `locale` header.
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard, lang]);

  // KPI strip — live via `overview` (PROLINE-3755 / 3853). The API returns no
  // change data for any KPI, and none of them is a last-30-days figure, so a
  // real value shows nothing under it; "no data yet" (0, or `null` for the
  // nullable Saudi Applicants % and Applicants < 5 Events) shows the KPI's own
  // supporting message instead — `saudi_applicants_percentage` renders "--"
  // rather than "0%" in that case.
  const stats = useMemo(() => {
    const empty = dashboard.stats.company.empty;
    // Upcoming + ongoing + completed only — excludes pending drafts and cancelled.
    const eventCounts = overview?.events_by_status?.counts || {};
    const eventsCount =
      (eventCounts.upcoming ?? 0) +
      (eventCounts.ongoing ?? 0) +
      (eventCounts.past ?? 0);
    const loyalCount = overview?.loyal_applicants_count ?? 0;
    const pendingCount = overview?.pending_applicant_requests_count ?? 0;
    // Shown exactly as the API returns it — no rounding.
    const saudiPercent = overview?.saudi_applicants_percentage;
    // Lifetime average of each event's junior share (PROLINE-3853); `null` when
    // no ongoing or past event carries a value.
    const juniorPercent = overview?.junior_applicants_percentage;

    const items = [
      { key: "totalEvents", value: formatCount(eventsCount), isEmpty: eventsCount === 0 },
      { key: "loyalApplicants", value: formatCount(loyalCount), isEmpty: loyalCount === 0 },
      {
        key: "saudiApplicants",
        noteKey: "saudiApplicantsNote",
        value: saudiPercent == null ? "--" : saudiPercent,
        valueSuffix: saudiPercent == null ? undefined : "%",
        isEmpty: saudiPercent == null,
      },
      {
        key: "pendingApplicantsRequest",
        value: formatCount(pendingCount),
        isEmpty: pendingCount === 0,
      },
      {
        key: "applicantsUnder5Events",
        noteKey: "applicantsUnder5EventsNote",
        value: juniorPercent == null ? 0 : juniorPercent,
        valueSuffix: "%",
        isEmpty: juniorPercent == null,
      },
    ];

    return items.map((item) => {
      const base = {
        id: item.key,
        label: dashboard.stats.company[item.key],
        labelNote: item.noteKey ? dashboard.stats.company[item.noteKey] : undefined,
        value: item.value,
        valueSuffix: item.valueSuffix,
      };

      if (item.isEmpty) {
        return { ...base, delta: empty[item.key], deltaTone: "muted" };
      }
      return base;
    });
  }, [dashboard, overview]);

  const eventsBreakdown = useMemo(() => {
    const counts = overview?.events_by_status?.counts || {};
    return Object.entries(EVENT_STATUS_API_KEY).map(([id, apiKey]) => ({
      id,
      value: counts[apiKey] ?? 0,
      // Same wording as the Events list page's own status tabs (shared.eventStatusOptions
      // is keyed like the API: apiKey, not the design-side id).
      label: shared.eventStatusOptions[apiKey],
    }));
  }, [shared, overview]);

  const eventsTotal = useMemo(
    () => overview?.events_by_status?.total ?? 0,
    [overview],
  );

  // Same wording as the Events list page's status tabs (shared.eventStatusOptions);
  // the table's completed rows use `completed` (see TOP_EVENT_STATUS).
  const topEventStatusLabels = useMemo(
    () => ({
      ...shared.eventStatusOptions,
      completed: shared.eventStatusOptions.past,
    }),
    [shared],
  );

  // events_overview (PROLINE-3826) arrives already ranked and capped at 3.
  const topEventRows = useMemo(
    () =>
      topEvents.map((event, index) => ({
        id: event.id,
        rank: index + 1,
        name: event.name,
        imageUrl: event.image_url,
        applicants: event.number_of_approved_requests,
        status: TOP_EVENT_STATUS[event.status] ?? event.status,
      })),
    [topEvents],
  );

  // events_overview?status=ongoing (PROLINE-3841). `null` figures stay `null`
  // so the card renders "--"; percentages are shown exactly as returned.
  const ongoingEvents = useMemo(
    () =>
      ongoingEventsData.map((event) => ({
        id: event.id,
        name: event.name,
        paymentPeriod: event.payment_period,
        metrics: {
          attendanceRate: event.attendance_rate,
          checkIn: event.check_ins_count,
          lateCheckIn: event.late_check_ins_count,
          checkOut: event.check_outs_count,
          noShow: event.no_shows_count,
        },
        applicants: event.number_of_approved_requests,
        newApplicantsPercent: event.junior_applicants_percentage,
        nationality: {
          count: event.number_of_approved_requests,
          saudiPercent: event.saudi_applicants_percentage,
          nonSaudiPercent: event.non_saudi_applicants_percentage,
        },
        dateRange: formatDateRange(event.launch_at, event.close_at, lang),
        openReportsCount: event.open_reports_count,
      })),
    [ongoingEventsData, lang],
  );

  // client_satisfaction (PROLINE-3826): one card per client with a submitted
  // survey; the score covers the client's whole history, not a recent window.
  const satisfactionItems = useMemo(() => {
    const copy = dashboard.satisfaction;

    return clientSatisfaction.map((item) => {
      const events = item.number_of_evaluated_events;
      const isOne = events === 1;

      return {
        id: item.id,
        name: item.name,
        score: item.overall_client_satisfaction,
        subtitle: (isOne ? copy.eventsEvaluatedOne : copy.eventsEvaluated).replace(
          "{count}",
          events,
        ),
        footerLabel: (isOne ? copy.basedOnOne : copy.basedOn).replace(
          "{count}",
          events,
        ),
      };
    });
  }, [dashboard, clientSatisfaction]);

  const recentActivityItems = useMemo(
    () =>
      recentActivity.map((entry) =>
        toActivityLogRow(entry, COMPANY_ACTIVITY_MODULE_BY_API_KEY),
      ),
    [recentActivity],
  );

  return (
    <div className="company-dashboard">
      <DashboardRefresh
        labels={{
          refresh: dashboard.refresh.action,
          refreshing: dashboard.refresh.refreshing,
          justNow: dashboard.refresh.justNow,
          minutesAgo: dashboard.refresh.minutesAgo,
          hoursAgo: dashboard.refresh.hoursAgo,
        }}
        updatedAt={updatedAt}
        loading={
          overviewLoading ||
          topEventsLoading ||
          ongoingEventsLoading ||
          clientSatisfactionLoading ||
          recentActivityLoading
        }
        onRefresh={fetchDashboard}
      />

      <DashboardHeader
        breadcrumbLabel={dashboard.breadcrumb}
        userName={login?.name || ""}
        userImage={login?.company?.image_url || login?.image_url}
        welcomeTemplate={dashboard.header.welcomeBack}
        welcomeFallback={dashboard.header.welcomeBackNoName}
        primaryAction={{
          label: dashboard.header.createEvent,
          onClick: () => history.push(ROUTE_PATHS.companyCreateEvent),
        }}
        stats={stats}
      />

      <div className="company-dashboard__grid">
        <TopEventsTable
          title={dashboard.topEvents.title}
          viewEventsLabel={dashboard.topEvents.viewEvents}
          columns={dashboard.topEvents.columns}
          statusLabels={topEventStatusLabels}
          rows={topEventRows}
          emptyTitle={dashboard.topEvents.empty.title}
          emptyDescription={dashboard.topEvents.empty.description}
          emptyActionLabel={dashboard.header.createEvent}
          onViewEvents={() => history.push(ROUTE_PATHS.companyEvents)}
          onEmptyAction={() => history.push(ROUTE_PATHS.companyCreateEvent)}
          onRowClick={(row) =>
            history.push(ROUTE_PATHS.companyViewEvent.replace(":id", row.id))
          }
        />

        <TotalEventsChart
          title={dashboard.totalEvents.title}
          totalLabel={dashboard.totalEvents.totalLabel}
          total={eventsTotal}
          breakdown={eventsBreakdown}
          emptyTitle={dashboard.totalEvents.empty.title}
          emptyDescription={dashboard.totalEvents.empty.description}
        />
      </div>

      <OngoingEventsData
        title={dashboard.ongoingEvents.title}
        viewEventsLabel={dashboard.ongoingEvents.viewEvents}
        events={ongoingEvents}
        metricLabels={dashboard.ongoingEvents.metrics}
        newApplicantsLabel={dashboard.ongoingEvents.newApplicants}
        paymentPeriodLabels={dashboard.ongoingEvents.paymentPeriod}
        nationalityCopy={{
          label: dashboard.ongoingEvents.nationality.label,
          applicantsLabel: dashboard.ongoingEvents.nationality.applicantsLabel,
          saudiLabel: dashboard.ongoingEvents.nationality.saudi,
          nonSaudiLabel: dashboard.ongoingEvents.nationality.nonSaudi,
        }}
        footerCopy={dashboard.ongoingEvents.footer}
        showMoreLabel={dashboard.ongoingEvents.showMore}
        showLessLabel={dashboard.ongoingEvents.showLess}
        emptyTitle={dashboard.ongoingEvents.empty.title}
        emptyDescription={dashboard.ongoingEvents.empty.description}
        createEventLabel={dashboard.header.createEvent}
        onViewEvents={() =>
          history.push(`${ROUTE_PATHS.companyEvents}?tab=${eventStatuses.ongoing}`)
        }
        onCreateEvent={() => history.push(ROUTE_PATHS.companyCreateEvent)}
        onEventAction={(id, tab) =>
          history.push(
            `${ROUTE_PATHS.companyViewEvent.replace(":id", id)}?tab=${tab}`,
          )
        }
      />

      <SatisfactionSection
        title={dashboard.satisfaction.title}
        overallLabel={dashboard.satisfaction.overall}
        bandLabels={dashboard.satisfaction.bands}
        items={satisfactionItems}
        showMoreLabel={dashboard.showMore}
        showLessLabel={dashboard.showLess}
        emptyTitle={dashboard.satisfaction.empty.title}
        emptyDescription={dashboard.satisfaction.empty.description}
      />

      <RecentActivityLog
        title={dashboard.recentActivity.title}
        items={recentActivityItems}
        modules={dashboard.recentActivity.modules}
        lang={lang}
        limit={10}
        visibleRows={7}
        fullLogsLabel={dashboard.recentActivity.fullLogs}
        onFullLogs={() => history.push(ROUTE_PATHS.companyActivityLog)}
        emptyTitle={dashboard.recentActivity.empty.title}
        emptyDescription={activityLog.company.noActivityDescription}
      />
    </div>
  );
};

export default CompanyDashboardPage;
